import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchPlant, updatePlant, type Plant } from "../lib/plants";
import { BackIcon } from "../components/icons";

export default function EditPlantScreen() {
  const navigate = useNavigate();
  const { plantId } = useParams<{ plantId: string }>();
  const [plant, setPlant] = useState<Plant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [nickname, setNickname] = useState("");
  const [frequency, setFrequency] = useState(7);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [light, setLight] = useState("");

  const load = useCallback(async () => {
    if (!plantId) return;
    try {
      const p = await fetchPlant(plantId);
      setPlant(p);
      setNickname(p.nickname || "");
      setFrequency(p.watering_frequency_days);
      setAmount(p.watering_amount || "");
      setNotes(p.watering_notes || "");
      setLight(p.light_needs || "");
    } catch (err) {
      console.error(err);
      alert("Could not load plant.");
    } finally {
      setLoading(false);
    }
  }, [plantId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave() {
    if (!plant) return;
    setSaving(true);
    try {
      await updatePlant(plant.id, {
        nickname: nickname.trim() || null,
        watering_frequency_days: frequency,
        watering_amount: amount,
        watering_notes: notes,
        light_needs: light,
      });
      navigate(-1);
    } catch (err) {
      console.error(err);
      alert("Could not save changes.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading-screen">Loading…</div>;
  if (!plant) return <div className="loading-screen">Plant not found</div>;

  return (
    <div className="app-shell">
      <div className="header-row">
        <button type="button" className="icon-btn" onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <h1 className="header-title">Edit Plant</h1>
        <div style={{ width: 40, height: 40 }} />
      </div>

      <div className="card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label className="section-label">NICKNAME</label>
        <input
          className="text-input"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
        />

        <label className="section-label">WATERING FREQUENCY (DAYS)</label>
        <input
          className="text-input"
          type="number"
          min={1}
          max={60}
          value={frequency}
          onChange={(e) => setFrequency(Number(e.target.value))}
        />

        <label className="section-label">AMOUNT</label>
        <input
          className="text-input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="light, moderate, or thorough"
        />

        <label className="section-label">NOTES</label>
        <textarea
          className="text-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
        />

        <label className="section-label">LIGHT</label>
        <input
          className="text-input"
          value={light}
          onChange={(e) => setLight(e.target.value)}
        />
      </div>

      <div className="sticky-cta-wrap">
        <button
          type="button"
          className="primary-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
